<template>
  <div class="sortBy">
    <select
      @change="onSortChange($event.target.value)"
      class="form-select mb-3"
      :disabled="isLoading"
      aria-label="Sort options"
    >
      <option value="newest">Sort by Newest</option>
      <option value="oldest">Sort by Oldest</option>
    </select>
    <div v-if="isLoading" class="text-muted small">Sorting data...</div>
  </div>
</template>

<script>
export default {
  name: "SortingComponent",
  data() {
    return {
      defaultSortingByValue: "created_date",
      isLoading: false,
      debounceTimer: null,
    };
  },
  props: {
    arrayDataForSorting: {
      type: Array,
      default: () => [], // Vue 3 fix: use function for array default
      validator: (value) => Array.isArray(value),
    },
    sortingByValue: {
      type: String,
      default: () => null, // Vue 3 fix: use function for consistency
    },
    debounceMs: {
      type: Number,
      default: 300,
      validator: (value) => value >= 0,
    },
  },
  emits: ["sorted-data-call", "sort-error"],
  methods: {
    onSortChange(value) {
      // Clear any existing debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Debounce the sort operation
      this.debounceTimer = setTimeout(() => {
        this.performSort(value);
      }, this.debounceMs);
    },

    performSort(value) {
      this.isLoading = true;

      try {
        if (value === "newest") {
          this.sortArrayByNewest();
        } else if (value === "oldest") {
          this.sortArrayByOldest();
        } else {
          this.handleError(`Invalid sort value: ${value}`);
        }
      } catch (error) {
        this.handleError(`Sort operation failed: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },

    sortArrayByNewest() {
      const sortedArray = this.getSortedArray((a, b) => {
        const dateA = this.parseDate(a);
        const dateB = this.parseDate(b);
        return dateB - dateA; // Newest first
      });

      if (sortedArray) {
        this.callSortedData(sortedArray);
      }
    },

    sortArrayByOldest() {
      const sortedArray = this.getSortedArray((a, b) => {
        const dateA = this.parseDate(a);
        const dateB = this.parseDate(b);
        return dateA - dateB; // Oldest first
      });

      if (sortedArray) {
        this.callSortedData(sortedArray);
      }
    },

    getSortedArray(compareFn) {
      if (!this.validateData()) {
        return null;
      }

      try {
        // Create a deep copy to avoid any mutation issues
        const arrayToSort = structuredClone(this.arrayDataForSorting);
        return arrayToSort.sort(compareFn);
      } catch (error) {
        this.handleError(`Failed to sort array: ${error.message}`);
        return null;
      }
    },

    parseDate(item) {
      const sortingKey = this.getSortingKey();
      const dateValue = item[sortingKey];

      if (!dateValue) {
        return new Date(0); // Return epoch for null/undefined dates
      }

      const parsedDate = new Date(dateValue);

      if (isNaN(parsedDate.getTime())) {
        // If date parsing fails, try to handle common formats
        const fallbackDate = this.tryFallbackDateParsing(dateValue);
        return fallbackDate || new Date(0);
      }

      return parsedDate;
    },

    tryFallbackDateParsing(dateValue) {
      // Handle common date formats that might not parse correctly
      if (typeof dateValue === "string") {
        // Try ISO format variations
        const isoAttempt = new Date(dateValue.replace(" ", "T"));
        if (!isNaN(isoAttempt.getTime())) {
          return isoAttempt;
        }

        // Try timestamp
        const timestamp = parseInt(dateValue);
        if (!isNaN(timestamp)) {
          return new Date(timestamp);
        }
      }

      return null;
    },

    validateData() {
      if (!Array.isArray(this.arrayDataForSorting)) {
        this.handleError("Data to sort must be an array");
        return false;
      }

      if (this.arrayDataForSorting.length === 0) {
        this.handleError("No data available to sort");
        return false;
      }

      const sortingKey = this.getSortingKey();
      const hasValidKey = this.arrayDataForSorting.some(
        (item) => item && typeof item === "object" && sortingKey in item,
      );

      if (!hasValidKey) {
        this.handleError(`Sorting key "${sortingKey}" not found in data`);
        return false;
      }

      return true;
    },

    getSortingKey() {
      return this.sortingByValue || this.defaultSortingByValue;
    },

    callSortedData(arrayToReturn) {
      this.$emit("sorted-data-call", arrayToReturn);
    },

    handleError(message) {
      console.warn(`Sorting Component: ${message}`);
      this.$emit("sort-error", {
        message,
        timestamp: new Date().toISOString(),
      });
    },
  },

  beforeUnmount() {
    // Clean up debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>

<style scoped>
.sortBy {
  position: relative;
}

.form-select {
  transition: all 0.2s ease;
}

.form-select:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  border-color: #80bdff;
}

.form-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-muted {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
